//React
import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import MediaQuery from "react-responsive";
import Popmenu from "./popmenu";
import AdminPopmenu from "./adminPopmenu";
import { isReadOnly as computeReadOnly, isAdmin } from "../../utilities/permissions";

//Assets
import logo from "../../assets/svgs/arcspan-logo.svg";
import help from "../../assets/svgs/help-circle.svg";
import hb from "../../assets/svgs/align-right.svg";
import close from "../../assets/svgs/x.svg";
import settings from "../../assets/svgs/settings.svg";
import toolIcon from "../../assets/svgs/tool.svg";
import switchIcon from "../../assets/svgs/switch.svg";
import icon from "../../assets/svgs/user.svg";

//CSS
import "./header.scss";
import "../../App.scss";
import { getTags } from "../../services/tag";
import { logout, getSession } from "../../services/auth";
import { isDemo } from "../../utilities/envUtils";

const profile = {
	img: icon,
	name: "Profile Name",
};

function Header() {
	const [tagId, setTagId] = useState(localStorage.getItem("activeSilo" ?? null));
	const nav = useNavigate();
	const _popMenus = { switch: false, user: false };
	const [open, setOpen] = useState(_popMenus);
	const [popMenus, setPopMenus] = useState(false);
	const [tagState, setTagState] = useState(null);
	const [roleState, setRoleState] = useState(null);
	const [readOnly, setReadOnly] = useState(false);
	const toggleMenu = () => {
		setOpen(!open);
	};
	const verifyAccess = async () => {
		const session = getSession();
		if (!session) {
			// No valid session (e.g. a logged-out deep link) - log in, then come back here
			logout({ returnHere: true });
			return;
		}

		setRoleState(session.role_id);
		setReadOnly(computeReadOnly(session.role_id));

		const userTags = await getTags();
		const tags = userTags.data;
		if (tagId == null) {
			localStorage.setItem("activeSilo", tags[0].tagId);
			localStorage.setItem("publisherSettings", JSON.stringify(tags[0].publisherSettings));
			window.dispatchEvent(new Event("storage"));
		}
		tags.map((tag) => {
			if (localStorage.getItem("activeSilo") == tag.tagId) {
				localStorage.setItem("publisherSettings", JSON.stringify(tag.publisherSettings));
				window.dispatchEvent(new Event("storage"));
			}
		});
		const tagsData = tags
			.map((tag) => ({
				type: "button",
				text: tag.name,
				active: localStorage.getItem("activeSilo") == tag.tagId,
				callback: () => [
					localStorage.setItem("activeSilo", tag.tagId),
					localStorage.setItem("publisherSettings", JSON.stringify(tag.publisherSettings)),
					window.dispatchEvent(new Event("storage")),
					nav("/"),
					setPopMenus({ switch: false }),
				],
			}))
			.sort((a, b) => a.text.localeCompare(b.text));
		return setTagState(tagsData);
	};

	useEffect(() => {
		window.addEventListener("storage", () => {
			setTagId(localStorage.getItem("activeSilo"));
		});
	}, []);

	useEffect(() => {
		verifyAccess();
	}, [tagId]);

	let popRef = useRef();

	useEffect(() => {
		let handler = (event) => {
			if (!popRef.current.contains(event.target)) {
				setPopMenus(false);
			}
		};
		document.body.addEventListener("click", handler);

		return () => {
			document.body.removeEventListener("click", handler);
		};
	});

	const handleLogout = () => {
		logout(); // This will clear token, stop refresh service, and redirect
	};
	return (
		<header
			className="header"
			ref={popRef}
		>
			<div className="header--inner">
					<div className="header--inner--item logo-wrapper">
						<Link to="/">
							<img
								src={logo}
								alt="ArcSpan"
							/>
						</Link>
					</div>
					<div
						className={`header--inner--item nav-wrapper ${open ? "mobile-open" : "mobile-closed"}`}
					>
						<NavLink
							className="nav-links"
							to="audience-manager"
						>
							Audience Manager
						</NavLink>
						{readOnly ? (
							<NavLink
								className="nav-links"
								to="audience-builder-v2"
								style={{ pointerEvents: "none", cursor: "default", opacity: "50%" }}
							>
								Audience Builder
							</NavLink>
						) : (
							<NavLink
								className="nav-links"
								to="audience-builder-v2"
							>
								Audience Builder
							</NavLink>
						)}
						<NavLink
							className="nav-links"
							to="audience-insights"
						>
							Audience Insights
						</NavLink>
						{isDemo(
							<NavLink
								className="nav-links"
								to="audience-arclens"
							>
								ArcLens
							</NavLink>,
							null
						)}
						<NavLink
							className="nav-links"
							to="arcspan-curated"
							style={{ pointerEvents: "none", cursor: "default", opacity: "50%" }}
						>
							CurationHub
						</NavLink>
						<NavLink
							className="nav-links"
							to="arctrax-reach"
							style={{ pointerEvents: "none", cursor: "default", opacity: "50%" }}
						>
							ArcTrax Reach+
						</NavLink>
					</div>
					<div className="header--inner--item user-wrapper">
						<div
							className="pop-menu-wrapper"
						>
							<button
								className="nav-links"
								onClick={() => setPopMenus((prev) => ({ switch: !prev.switch }))}
								title="Switch ArcTag"
							>
								<img
									src={switchIcon}
									alt="Switch User"
								/>
							</button>
							{popMenus.switch && (
								<Popmenu
									items={tagState ?? []}
									searchable={(tagState?.length ?? 0) > 10}
									searchPlaceholder="Search silos..."
								/>
							)}
						</div>

						<NavLink
							className="nav-links"
							to="help"
							title="Help"
						>
							<img
								src={help}
								alt="Help"
							/>
						</NavLink>
						{isAdmin(roleState) && (
						<div className="pop-menu-wrapper">
							<button
								className="nav-links admin-tools-btn"
								onClick={() => setPopMenus((prev) => ({ admin: !prev.admin }))}
								title="Admin Tools"
							>
								<img
									src={toolIcon}
									alt="Admin Tools"
								/>
							</button>
							{popMenus.admin && (
								<AdminPopmenu
									onClose={() => setPopMenus({})}
									items={[
										{
											text: "AI Audience Explorer",
											description:
												"Build audiences from natural language using AI-powered targeting",
											to: "/ai-audience-explorer-v2",
										},
										{
											text: "AI Audience Explorer (Demo)",
											description:
												"Demo mode with mock data — no API calls",
											to: "/demo/ai-audience-explorer-v2",
										},
										{
											text: "AI Audience Explorer History",
											description:
												"Review past AI Audience Explorer sessions, prompts, and results",
											to: "/admin/ai-generations",
										},
										{
											text: "Admin Dashboards",
											description:
												"Daily data panels computed by Airflow, grouped by topic — NLP blocked pages first",
											to: "/admin/dashboards",
										},
										{
											text: "Debug AI Audience Generation",
											description:
												"Debug utility for testing AI audience generation with various LLM models",
											to: "/debug/audience-generate",
										},
										{
											text: "Insights Deck Builder",
											description:
												"Build branded campaign insights decks from audience and performance data",
											to: "/insights-deck",
										},
										{
											text: "Ad Quality Analyzer",
											description:
												"Analyze web pages for ad density, refresh rates, and intrusive ads",
											to: "/ad-quality",
											newTab: true,
										},
										{
											text: "ArcLens",
											description:
												"AI-powered content analysis for brand safety, sentiment, and IAB taxonomy",
											to: "/arclens",
											newTab: true,
										},
									]}
								/>
							)}
						</div>
					)}
						{/* hide if pub user */}
						{roleState === 1 || roleState === 2 ?
							<NavLink
								className="nav-links"
								to="/settings"
								title="Settings"
							>
								<img
									src={settings}
									alt="Settings"
								/>
							</NavLink> : ""
						}
						<div
							className="pop-menu-wrapper"
						>
							<button
								className="nav-links"
								onClick={() => setPopMenus((prev) => ({ user: !prev.user }))}
								title="Profile"
							>
								<img
									className="profile"
									src={profile.img}
									alt={profile.name}
								/>
							</button>
							{popMenus.user && (
								<Popmenu
									items={[
										{
											type: "button",
											text: "Logout",
											active: false,
											callback: function () {
												handleLogout();
											},
										},
									]}
								/>
							)}
						</div>

						<MediaQuery maxWidth={1150}>
							<div className="mobile-menu">
								<button
									type="button"
									onClick={toggleMenu}
								>
									<img
										className="hamburger"
										src={open ? close : hb}
										alt="Toggle Menu"
									/>
								</button>
							</div>
						</MediaQuery>
					</div>
				</div>
		</header>
	);
}

export default Header;
